import { useState } from "react";
import { useIntl } from "react-intl";
import { useScroll } from "src/ScrollContext";
import { sendAnalyticsInteractionEvent } from "src/analytics/sendAnalyticsEvent";
import { SearchButton } from "src/components/SearchButton/SearchButton";
import { Icon } from "src/design-system/components/Icon/Icon";
import Passenger from "src/design-system/icons/User.svg?react";
import color from "src/design-system/tokens/color";
import SearchCombinedPicker from "src/domain/SegmentScreen/SegmentHeader/CombinedPicker/Experiment/SearchCombinedPicker";
import { headerMessages } from "src/domain/TopNav/TopNav.messages";
import { isSameDate, numDaysBetweenTwoDates } from "src/utils/dateTime";
import {
  getDefaultCheckInDate,
  getDefaultCheckOutDate,
  type RoomDetail,
  useHotelsContext,
} from "../../HotelsContext";
import type { HotelFetchUpdates } from "../../HotelsScreen";
import { useHotelStayAndRoomDetailsText } from "../../utils";
import { Combobox } from "../Combobox";
import messages from "../HotelSearch.messages";
import { RoomsForm } from "../RoomsForm/RoomsForm";
import { DestinationInput } from "./DestinationInput";
type RequestTrigger = "DateChange" | "RoomDetailsChange";

export function DatesHotelSearchBar() {
  const intl = useIntl();
  const {
    stayDatesUI,
    setStayDatesUI,
    initiateFakeHotelLoad,
    roomDetailsUI,
    setRoomDetailsUI,
    setHotelListQueryParams,
    filtersState,
    setIsDatePickerOpen,
    setShowDatesPopover,
  } = useHotelsContext();
  const { setScrollTop } = useScroll();

  // Date picker init
  const { roomDetailsText } = useHotelStayAndRoomDetailsText();
  const [initialDate, setInitialDate] = useState<Date>(getDefaultCheckInDate());

  // Room details init
  const [isRoomDetailsExpanded, setIsRoomDetailsExpanded] = useState(false);
  const [freshFormData, setFreshFormData] =
    useState<RoomDetail[]>(roomDetailsUI);

  function handleApiSearch(trigger: RequestTrigger, dates?: [Date, Date]) {
    if (dates) {
      refetchHotelList({
        arrival: dates[0],
        departure: dates[1],
      });
    } else {
      refetchHotelList({
        roomDetails: freshFormData,
      });
    }
    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Initiate:hotelListRequest",
      label: `${trigger}`,
    });
  }

  function refetchHotelList(updates: HotelFetchUpdates) {
    setHotelListQueryParams((state) => {
      return {
        ...state,
        arrivalDate: updates.arrival ?? stayDatesUI?.[0],
        departureDate: updates.departure ?? stayDatesUI?.[1],
        roomDetails: updates.roomDetails ?? roomDetailsUI,
        filters: filtersState,
      };
    });
  }

  function handleDateRangeSelected(startDate: Date, endDate?: Date) {
    if (!startDate) {
      return;
    }

    setShowDatesPopover(false);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const daysUntilDate = numDaysBetweenTwoDates(today, startDate);
    let gaLabelSuffix = "-dateRangeSelected";

    // If the user has only selected a check-in date, then we don't want to update the stayDatesUI yet.
    if (!endDate) {
      setInitialDate(startDate);
      setStayDatesUI(undefined);
      sendAnalyticsInteractionEvent({
        category: "Hotels",
        action: "Click:HotelDateRangeCalendar",
        label: `firstDate`,
      });
      return;
    }

    // Auto selects the default date range if same date clicked.
    if (isSameDate(startDate, endDate)) {
      endDate = getDefaultCheckOutDate(endDate);
      gaLabelSuffix = `${gaLabelSuffix}-sameDateClick`;
    }

    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:HotelDateRangeCalendar",
      label: `daysInAdvance:${daysUntilDate}${gaLabelSuffix}`,
    });

    const dateRange: [Date, Date] = [startDate, endDate];
    setStayDatesUI(dateRange);
    handleApiSearch("DateChange", dateRange);
    setIsDatePickerOpen(false);
    setInitialDate(startDate);
    setScrollTop(0);
  }

  function handleRemoveEndDate() {
    const startDate = stayDatesUI?.[0] ?? initialDate;
    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:RemoveEndDate",
    });
    handleApiSearch("DateChange", [
      startDate,
      new Date(startDate.getTime() + 24 * 60 * 60 * 1000),
    ]);
  }

  function handleRoomDetailsFormSubmit() {
    const sumAdults = roomDetailsUI.reduce((sum, room) => sum + room.adults, 0);
    const sumChildren = roomDetailsUI.reduce(
      (sum, room) => sum + room.children.length,
      0,
    );
    const sumRooms = roomDetailsUI.length;

    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:RoomsDetailsFormSearch",
      label: `adults${sumAdults}:children${sumChildren}:rooms${sumRooms}`,
    });

    setRoomDetailsUI(freshFormData);
    handleApiSearch("RoomDetailsChange");
    setIsRoomDetailsExpanded(false);
    setScrollTop(0);
  }

  function handleSearch() {
    initiateFakeHotelLoad();
    sendAnalyticsInteractionEvent({
      category: "Hotels",
      action: "Click:search-button",
      label: "no-request",
    });
  }

  function resetRoomDetailsForm() {
    setFreshFormData(roomDetailsUI); // reset form to what was in context previously.
  }

  return (
    <div className="relative flex items-center rounded-full bg-neutral-700">
      <DestinationInput />
      <span className="flex h-6 min-w-px flex-0 bg-text-secondary opacity-10" />

      <SearchCombinedPicker
        className="ml-auto"
        beginLabel={intl.formatMessage(messages.checkInLabel)}
        endLabel={intl.formatMessage(messages.checkOutLabel)}
        promptBeginText={intl.formatMessage(messages.promptBeginText)}
        promptEndText={intl.formatMessage(messages.promptEndText)}
        beginDate={stayDatesUI?.[0] ?? initialDate}
        endDate={stayDatesUI?.[1]}
        onDatesChange={handleDateRangeSelected}
        onRemoveEndDate={handleRemoveEndDate}
        onSelectEndDate={(newReturnDate) => {
          handleDateRangeSelected(
            stayDatesUI?.[0] ?? initialDate,
            newReturnDate,
          );
        }}
        onBeginDateToggle={() => {
          sendAnalyticsInteractionEvent({
            category: "Hotels",
            action: "Click:hotels-dates:checkInDate",
            label: "open",
          });
        }}
        onEndDateToggle={() => {
          sendAnalyticsInteractionEvent({
            category: "Hotels",
            action: "Click:hotels-dates:checkOutDate",
            label: "open",
          });
        }}
      />

      <span className="flex h-6 min-w-px flex-0 bg-text-secondary opacity-10" />
      <Combobox
        className="h-full max-w-50 md:flex-1"
        id="hotel-search-room-details-combobox"
        icon={
          <Icon>
            <Passenger
              aria-label={intl.formatMessage(headerMessages.who)}
              color={color.icon.primary}
            />
          </Icon>
        }
        label={intl.formatMessage(messages.guestsLabel)}
        ariaLabel={intl.formatMessage(messages.roomDetailsAriaLabel)}
        inputText={roomDetailsText}
        onClickCta={handleRoomDetailsFormSubmit}
        ctaMessage={intl.formatMessage(messages.done)}
        isChildExpanded={isRoomDetailsExpanded}
        setIsChildExpanded={setIsRoomDetailsExpanded}
        showMobileControls={true}
        onBackgroundClick={resetRoomDetailsForm}
        onDismissClick={resetRoomDetailsForm}
        onClickOpen={() => {
          sendAnalyticsInteractionEvent({
            category: "Hotels",
            action: "Click:hotels-travellers-form",
            label: "open",
          });
        }}
        hotelInput
        dropdownBorderRadius="rounded_xl"
      >
        <RoomsForm
          freshFormData={freshFormData}
          setFreshFormData={setFreshFormData}
          onCtaClick={handleRoomDetailsFormSubmit}
          ctaMessage={intl.formatMessage(messages.done)}
        />
      </Combobox>

      <SearchButton onPress={handleSearch} hidePulse />
    </div>
  );
}
